import { useMemo } from 'react';
import {
  isDateLocked,
  TIME_LOCK_DOC_ID,
  type TimeLock,
} from '@altyor-kronos/shared-types';
import { usePolledResource } from '../../core/data/usePolledResource';
import { useAuth } from '../../core/auth/useAuth';
import { apiList, apiPut } from '../../core/data/restClient';

/**
 * Clôture mensuelle des saisies, côté écran.
 *
 * L'autorité reste kronos-api (`authz/policies/timeLock.ts`) : ce hook ne fait
 * que refléter sa décision pour griser ce qui ne passera pas. Il s'appuie sur
 * le MÊME `isDateLocked` que le serveur — sinon l'UI proposerait des lignes
 * que l'API refuse à l'enregistrement.
 */
export interface TimeLockState {
  /** Dernier mois clôturé, 'YYYY-MM'. `null` = rien n'est gelé. */
  lockedThrough: string | null;
  /**
   * Vrai si l'utilisateur courant ne peut PAS écrire à cette date.
   *
   * « Clôturé » et non « verrouillé » : `WeeklyBreakdown` et `CalendarGrid`
   * emploient déjà `locked` pour le week-end et les jours fériés, qu'un clic
   * rouvre. Une clôture mensuelle, elle, ne se rouvre pas côté utilisateur.
   */
  isMonthClosed: (d: Date) => boolean;
  /** `admin` passe outre la clôture. */
  canBypass: boolean;
  loading: boolean;
}

export function useTimeLock(): TimeLockState {
  const { user } = useAuth();
  const { rows, loading } = usePolledResource<TimeLock & { id: string }>(
    'settings',
    () => apiList<TimeLock & { id: string }>('/settings'),
  );
  const canBypass = user?.roles?.includes('admin') ?? false;

  const lockedThrough = useMemo(
    () => rows.find((r) => r.id === TIME_LOCK_DOC_ID)?.lockedThrough ?? null,
    [rows],
  );

  return useMemo(
    () => ({
      lockedThrough,
      canBypass,
      loading,
      // Pendant le chargement on ne gèle rien : un faux verrou bloquerait
      // la saisie du jour à chaque ouverture de page. Le serveur tranche.
      isMonthClosed: (d: Date) => !canBypass && !loading && isDateLocked(d, lockedThrough),
    }),
    [lockedThrough, canBypass, loading],
  );
}

/** Pose (ou déplace) la clôture. Le serveur n'accepte que `admin` | `finance`. */
export async function setTimeLock(lockedThrough: string | null): Promise<void> {
  await apiPut(`/settings/${TIME_LOCK_DOC_ID}`, { lockedThrough });
}
